import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { getToken } from '../../services/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeader() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Instructor: queue list ────────────────────────────────────────────────────

export function useInstructorCVQueue({ status = '', skip = 0, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  params.set('skip', String(skip));
  params.set('limit', String(limit));

  return useQuery({
    queryKey: ['instructor', 'cv', 'queue', status, skip, limit],
    queryFn: async () => {
      const res = await axios.get(`${API}/instructor/cv/queue?${params.toString()}`, {
        headers: authHeader(),
      });
      // Backend returns { items: [...], total: N, skip, limit }
      return res.data.items ?? [];
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: !!getToken(),
  });
}

// ── Instructor: single CV detail ─────────────────────────────────────────────

export function useInstructorCVDetail(id) {
  return useQuery({
    queryKey: ['instructor', 'cv', 'detail', id],
    queryFn: async () => {
      const res = await axios.get(`${API}/instructor/cv/${id}`, {
        headers: authHeader(),
      });
      return res.data;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: !!id && !!getToken(),
  });
}

// ── Instructor: claim ────────────────────────────────────────────────────────

export function useClaimCV() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await axios.post(`${API}/instructor/cv/${id}/claim`, {}, {
        headers: authHeader(),
      });
      return res.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['instructor', 'cv', 'queue'] });
      queryClient.invalidateQueries({ queryKey: ['instructor', 'cv', 'detail', id] });
    },
  });
}

// ── Instructor: unclaim ──────────────────────────────────────────────────────

export function useUnclaimCV() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await axios.post(`${API}/instructor/cv/${id}/unclaim`, {}, {
        headers: authHeader(),
      });
      return res.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['instructor', 'cv', 'queue'] });
      queryClient.invalidateQueries({ queryKey: ['instructor', 'cv', 'detail', id] });
    },
  });
}

// ── Instructor: save edits ───────────────────────────────────────────────────

export function useSaveCVEdits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, instructor_edits, instructor_note }) => {
      const res = await axios.put(
        `${API}/instructor/cv/${id}/edits`,
        { instructor_edits, instructor_note },
        { headers: authHeader() }
      );
      return res.data;
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['instructor', 'cv', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['instructor', 'cv', 'queue'] });
    },
  });
}

// ── Instructor: approve ──────────────────────────────────────────────────────

export function useApproveCV() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await axios.post(`${API}/instructor/cv/${id}/approve`, {}, {
        headers: authHeader(),
      });
      return res.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['instructor', 'cv', 'queue'] });
      queryClient.invalidateQueries({ queryKey: ['instructor', 'cv', 'detail', id] });
    },
  });
}

// ── Instructor: request changes ──────────────────────────────────────────────

export function useRequestCVChanges() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, student_message }) => {
      const res = await axios.post(
        `${API}/instructor/cv/${id}/request-changes`,
        { student_message },
        { headers: authHeader() }
      );
      return res.data;
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['instructor', 'cv', 'queue'] });
      queryClient.invalidateQueries({ queryKey: ['instructor', 'cv', 'detail', id] });
    },
  });
}
